import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Box
} from "@mui/material"
import { supabase, supabaseAdminClient } from "../../Supabase/supabaseRealtimeClient"
import DependenteDialog, { Dependente } from "./DependenteDialog"
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog"

interface DependenteListDialogProps {
  open: boolean
  onClose: () => void
  titular: { id: number; nome: string } | null
}

const DependenteListDialog: React.FC<DependenteListDialogProps> = ({ open, onClose, titular }) => {
  const [dependentes, setDependentes] = useState<Dependente[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedDep, setSelectedDep] = useState<Dependente | undefined>(undefined)
  const [isEditing, setIsEditing] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [depToDelete, setDepToDelete] = useState<Dependente | null>(null)

  const fetchDependentes = async () => {
    if (!titular) return
    setLoading(true)
    try {
      const url = process.env.REACT_APP_SUPABASE_URL
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
      const accessToken = localStorage.getItem("accessToken")

      const response = await fetch(
        `${url}/rest/v1/dependentes?cliente_id=eq.${titular.id}&deletado=is.false&order=nome.asc`,
        {
          method: "GET",
          headers: {
            apikey: String(serviceKey),
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Erro ao buscar dependentes: ${response.status}`)
      }

      const data = await response.json()
      setDependentes(data || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && titular) {
      fetchDependentes()
    }
  }, [open, titular])

  const handleAdd = () => {
    setSelectedDep(undefined)
    setIsEditing(false)
    setDialogOpen(true)
  }

  const handleEdit = (dep: Dependente) => {
    setSelectedDep(dep)
    setIsEditing(true)
    setDialogOpen(true)
  }

  const handleDeleteOpen = (dep: Dependente) => {
    setDepToDelete(dep)
    setDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!depToDelete) return
    setLoading(true)
    try {
      const url = process.env.REACT_APP_SUPABASE_URL
      const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
      const accessToken = localStorage.getItem("accessToken")

      // 1. Marcar como deletado no banco (PATCH)
      const response = await fetch(
        `${url}/rest/v1/dependentes?id=eq.${depToDelete.id}`,
        {
          method: "PATCH",
          headers: {
            apikey: String(serviceKey),
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            Prefer: "return=minimal"
          },
          body: JSON.stringify({ 
            deletado: true, 
            email: `deletado${depToDelete.id}@delete.com` 
          })
        }
      )

      if (!response.ok) {
        throw new Error(`Erro ao deletar: ${response.status}`)
      }

      // 2. Opcional: Deletar do Auth do Supabase se tiver user_id
      // Como o supabaseAdminClient usa o serviceRoleKey, ele já ignora RLS
      const { data: fullDep } = await supabase.from("dependentes").select("user_id").eq("id", depToDelete.id).single()
      if (fullDep?.user_id) {
        await supabaseAdminClient.auth.admin.deleteUser(fullDep.user_id, true)
      }

      alert("Dependente excluído.")
      fetchDependentes()
    } catch (err: any) {
      alert(`Erro ao excluir: ${err.message}`)
    } finally {
      setLoading(false)
      setDeleteOpen(false)
      setDepToDelete(null)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Dependentes de {titular?.nome}</Typography>
          <Button variant="contained" color="primary" onClick={handleAdd}>
            Adicionar Dependente
          </Button>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dependentes.length > 0 ? (
                    dependentes.map(dep => (
                      <TableRow key={dep.id}>
                        <TableCell>{dep.nome}</TableCell>
                        <TableCell>{dep.email}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleEdit(dep)}>Editar</Button>
                          <Button size="small" color="error" onClick={() => handleDeleteOpen(dep)}>Excluir</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">Nenhum dependente cadastrado.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {titular && (
        <DependenteDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          initialData={selectedDep}
          titularId={titular.id}
          isEditing={isEditing}
          onSave={fetchDependentes}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={depToDelete?.nome}
      />
    </>
  )
}

export default DependenteListDialog
